// Constants
import { WatchlistProviders } from 'app/modules/detectionModels/components/scenarioWidgets/watchlist/enums';
import { UNLOADED_FLAGS_KEY } from 'app/shared/featureFlags/constants';

export enum FeatureFlagsActionTypes {
  SET_FEATURE_FLAGS = '@@featureFlags/SET_FEATURE_FLAGS',
}

// This enum should only contain active flags
// (makes archiving easier as tsc will complain when you pluck out a no-longer-needed flag)
export enum FeatureFlag {
  // Investigations
  AI_ADMIN_DASHBOARD = 'ai-admin-dashboard',
  EDIT_CUSTOM_DATA = 'edit-custom-data',
  ALERT_COMPONENT_BUTTONS = 'alert-component-buttons',
  SHOW_PAYER_PAYEE_TABS = 'show-payer-payee-tabs',
  REMOVE_DISPLAY_TAGS = 'remove-display-tags',
  BULK_ACTION_LIMIT = 'bulk-action-limit',
  NEW_BULK_ACTIONS = 'new-bulk-actions',
  ALERT_FROM_ALERT = 'alert-from-alert',
  WORKAROUND_HIDE_CURRENCY_CONFIG = 'workaround-hide-currency-code',
  DISPOSITION_ACTIONS = 'disposition-actions',
  WATCHLIST_ALERTS = 'watchlist-alerts',
  ENFORCEMENTS_SCREENING = 'enforcements-screening',
  ALERT_TABLE_AMOUNT_COLUMN_SKELETON_LOADING = 'alert-table-amount-column-skeleton-loading',
  QUALITY_CYCLE_LIMIT = 'quality-cycle-limit',
  WATCHLIST_PROVIDER = 'watchlist-provider',
  BULK_RESOLVE_FROM_QUEUED_VIEWS = 'bulk-resolve-from-queued-views',
  ADD_CASES_TO_SAR = 'ADD_CASES_TO_SAR',
  FINCEN_314A = '314a',
  FINTRAC_STR = 'fintrac-str',
  FINTRAC_STR_UPLOAD = 'fintrac-str-upload',
  FINTRAC_STR_AUTOFILL_WORKFLOW = 'fintrac-str-autofill-workflow',
  FINCEN_DOEP_FILING = 'FINCEN_DOEP_FILING',
  GOAML_V2 = 'GOAML_V2',
  // SC #116590 — gates entity/instrument grouping for both auto-assignment
  // (schedule types) and the "Get More" pull. Value matches the LD flag key
  // the backend (dumbledore/patronus) reads verbatim, so one flag drives all.
  ROUND_ROBIN_GROUP_BY_ENTITY_INSTRUMENT = 'ROUND_ROBIN_GROUP_BY_ENTITY_INSTRUMENT',
  TEAM_BASED_WORKFLOW_PERMISSIONS = 'team-based-workflow-permissions',
  FINTRAC_ERROR_CARDS = 'fintrac-error-cards',
  CLOSE_ASSOCIATED_ALERTS_ON_ESCALATION = 'close-associated-alerts-on-escalation',

  // AI
  RULE_FEEDBACK_LOOP = 'rule-feedback-loop',
  AI_AGENT = 'ai-agent',
  AI_AGENT_DATA_EXPLORATION = 'ai-agent-data-exploration',
  ASK_YOUR_ALERT = 'ask-your-alert',
  BUILD_YOUR_OWN_AGENT = 'build-your-own-agent',
  AI_AGENT_BACKTESTING = 'ai-agent-backtesting',
  ENABLE_FILING_AI_AGENTS = 'enable-filing-ai-agents',
  AI_DECISIONING_V0 = 'ai-decisioning-v-0',
  ONLINE_RESEARCH_BYOT = 'byot-online-research',
  AI_AGENT_BYOT_MATCHLISTS = 'ai-agent-byot-matchlists',
  G_GROUNDING_ONLINE_RESEARCH = 'g-grounding-online-research',
  GREENDOT_PIVOT_TABLES = 'greendot-pivot-tables',
  MCP_SERVER_ENABLED = 'mcp-server-enabled',

  // Delta Lake Transition Flags
  // Enable loading CGDO on demand when an alert/case/sar is looked at
  PG_CACHE_AD_HOC_DATA_LOADER = 'PG_CACHE_AD_HOC_DATA_LOADER',

  // Alert Pages
  CM_DELTA_LAKE_ALERT_LIST = 'CM_DELTA_LAKE_ALERT_LIST',
  CM_DELTA_LAKE_ALERT_DETAIL = 'CM_DELTA_LAKE_ALERT_DETAIL',
  CM_DELTA_LAKE_ALERT_TXN_ANALYSIS = 'CM_DELTA_LAKE_ALERT_TXN_ANALYSIS',
  CM_DELTA_LAKE_ALERT_TXN_LIST = 'CM_DELTA_LAKE_ALERT_TXN_LIST',
  CM_DELTA_LAKE_ALERT_ACTION_LIST = 'CM_DELTA_LAKE_ALERT_ACTION_LIST',

  // Case Pages
  CM_DELTA_LAKE_CASE_LIST = 'CM_DELTA_LAKE_CASE_LIST',
  CM_DELTA_LAKE_CASE_CGDO_SEARCH = 'CM_DELTA_LAKE_CASE_CGDO_SEARCH',
  CM_DELTA_LAKE_CASE_DETAIL = 'CM_DELTA_LAKE_CASE_DETAIL',
  CM_DELTA_LAKE_CASE_TXN_ANALYSIS = 'CM_DELTA_LAKE_CASE_TXN_ANALYSIS',
  CM_DELTA_LAKE_CASE_ENTITIES = 'CM_DELTA_LAKE_CASE_ENTITIES',
  CM_DELTA_LAKE_CASE_ACTION_EVENTS = 'CM_DELTA_LAKE_CASE_ACTION_EVENTS',

  // Filing Pages
  CM_DELTA_LAKE_FILING_SAR_LIST = 'CM_DELTA_LAKE_FILING_SAR_LIST',
  CM_DELTA_LAKE_FILING_SAR_CREATE = 'CM_DELTA_LAKE_FILING_SAR_CREATE',
  CM_DELTA_LAKE_FILING_CTR_LIST = 'CM_DELTA_LAKE_FILING_CTR_LIST',
  CM_DELTA_LAKE_FILING_CTR_CREATE = 'CM_DELTA_LAKE_FILING_CTR_CREATE',
  CM_DELTA_LAKE_FILING_GOAML_LIST = 'CM_DELTA_LAKE_FILING_GOAML_LIST',
  CM_DELTA_LAKE_FILING_GOAML_CREATE = 'CM_DELTA_LAKE_FILING_GOAML_CREATE',
  CM_DELTA_LAKE_FILING_SAR_DETAIL = 'CM_DELTA_LAKE_FILING_SAR_DETAIL',
  CM_DELTA_LAKE_FILING_CTR_DETAIL = 'CM_DELTA_LAKE_FILING_CTR_DETAIL',
  CM_DELTA_LAKE_GOAML_DETAIL = 'CM_DELTA_LAKE_GOAML_DETAIL',
  CM_DELTA_LAKE_FILING_FINTRAC_CREATE = 'CM_DELTA_LAKE_FILING_FINTRAC_CREATE',

  // Dashboard Pages
  CM_DELTA_LAKE_FRAUD_DASHBOARD = 'CM_DELTA_LAKE_FRAUD_DASHBOARD',

  // Data Explorer Pages
  CM_DELTA_LAKE_TXN_EVENT_LIST = 'CM_DELTA_LAKE_TXN_EVENT_LIST',
  CM_DELTA_LAKE_TXN_EVENT_DETAIL = 'CM_DELTA_LAKE_TXN_EVENT_DETAIL',
  CM_DELTA_LAKE_ACTION_EVENT_LIST = 'CM_DELTA_LAKE_ACTION_EVENT_LIST',
  CM_DELTA_LAKE_ACTION_EVENT_DETAIL = 'CM_DELTA_LAKE_ACTION_EVENT_DETAIL',
  CM_DELTA_LAKE_INSTRUMENT_LIST = 'CM_DELTA_LAKE_INSTRUMENT_LIST',
  CM_DELTA_LAKE_INSTRUMENT_DETAIL = 'CM_DELTA_LAKE_INSTRUMENT_DETAIL',
  CM_DELTA_LAKE_INSTRUMENT_RELATIONS = 'CM_DELTA_LAKE_INSTRUMENT_RELATIONS',
  CM_DELTA_LAKE_INSTRUMENT_TXN_EVENTS = 'CM_DELTA_LAKE_INSTRUMENT_TXN_EVENTS',
  CM_DELTA_LAKE_ENTITY_LIST = 'CM_DELTA_LAKE_ENTITY_LIST',
  CM_DELTA_LAKE_ENTITY_DETAIL = 'CM_DELTA_LAKE_ENTITY_DETAIL',
  CM_DELTA_LAKE_ENTITY_RISK_SCORE = 'CM_DELTA_LAKE_ENTITY_RISK_SCORE',
  CM_DELTA_LAKE_NETWORK_ANALYSIS = 'CM_DELTA_LAKE_NETWORK_ANALYSIS',
  CM_DELTA_LAKE_ENTITY_RELATIONS = 'CM_DELTA_LAKE_ENTITY_RELATIONS',
  CM_DELTA_LAKE_ENTITY_TXN_EVENTS = 'CM_DELTA_LAKE_ENTITY_TXN_EVENTS',
  CM_DELTA_LAKE_ENTITY_ACTION_EVENTS = 'CM_DELTA_LAKE_ENTITY_ACTION_EVENTS',
  CM_DELTA_LAKE_ENTITY_GEOLOCATION = 'CM_DELTA_LAKE_ENTITY_GEOLOCATION',
  CM_DELTA_LAKE_HASHED_ID_ROUTES = 'CM_DELTA_LAKE_HASHED_ID_ROUTES',

  // DIM
  DARK_WEB_ALERTS = 'dark-web-alerts',
  CONSORTIUM_DMB = 'consortium-dmb',
  CONSORTIUM_ALERTS_SUMMARY = 'consortium-alerts-summary',
  CONSORTIUM_ALERTS_V2 = 'consortium-alerts-v2',

  // DM
  SIXTRAN_SNOWFLAKE_EXPECTED_LAG = 'sixtran-snowflake-expected-lag',
  FRAUD_TRANSACTION_RULES = 'fraud-transaction-rules',
  RULE_DELAY = 'rule-delay',
  ACH_RISK_SCORE_SCENARIO_ENABLED = 'ach-risk-score-scenario-enabled',
  ACH_RISK_SCORE_FIELD = 'ach-risk-score-field',
  RULE_EVENT_SCHEMA_V2_1 = 'rule-event-schema-v-2-1',
  FOCSEC_FIELDS_ENABLED = 'focsec-fields-enabled',
  DMB_CUSTOM_TXN_DATETIME = 'dmb-custom-txn-datetime',
  VELOCITY_RTR_ENTITY_INSTRUMENT_AGGREGATION_FILTERS = 'rtr-entity-instrument-aggregation-filters',
  RTR_ENTITY_ALLOWLISTING = 'rtr-entity-allowlisting',
  RTR_TRM_ENABLED = 'rtr-trm-enabled',
  RULE_RECOMMENDATIONS = 'rule-recommendations',
  RULE_RECS_V2_UI = 'rule-recs-v2-ui',
  RULE_RECS_CREATE_RULE = 'rule-recs-create-rule',
  DMB_FORM_REFACTOR = 'dmb-form-refactor',
  RULE_BANKING_ACTIONS = 'rule-banking-actions',
  RULE_LOCAL_SAVE_STATE = 'rule-local-save-state',
  WATCHLIST_COMBINED_JOURNEYS = 'watchlist-combined-journeys',
  AI_TEXT_TO_RULE = 'ai-text-to-rule',
  FINCEN_ADVISORY_TEMPLATES = 'fincen-advisory-templates',
  DMB_AI_FUZZY_SCORE = 'dmb-ai-fuzzy-score',
  GLOBAL_VARIABLES = 'global-variables',
  THRESHOLD_EXPLORER = 'threshold-explorer',
  THRESHOLD_EXPLORER_DASHBOARDS = 'threshold-explorer-dashboards',
  SR_TRM_DMB_ENABLED = 'sr-trm-dmb-enabled',
  DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_ENABLED = 'alert-hit-cgdo-historical-lookup-enabled',
  DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_FOR_ALL_AGENTS = 'alert-hit-cgdo-historical-lookup-for-all-agents',
  RULES_AVOID_ALERT_GENERATION = 'rules-avoid-alert-generation',

  // DE
  ENTITY_PHONE_NUMBER_SEARCH_V2 = 'entity-phone-number-search-v2',
  CM_DELTA_LAKE_SEARCH_V2_USE_DL_HASH_ID = 'CM_DELTA_LAKE_SEARCH_V2_USE_DL_HASH_ID',
  FFIP_ENRICHMENT_MATCHING = 'ffip-enrichment-matching',

  // DevOps
  RULE_LIFECYCLE_BANNER = 'rule-lifecycle-banner',

  // Misc
  SESSION_REPLAY = 'sentry-session-replay',
  FEEDBACK_SURVEY_ID = 'feedback-survey-id',
  ALLOW_AI_AGENT_RE_RUN = 'ALLOW_AI_AGENT_RE_RUN',
  G2_REVIEW = 'g2-review',
  EQUIFAX_DEMO = 'equifax-demo',
  FRAUD_FIGHTER_LOGO = 'fraud-fighter-logo',
  SAR_AUTOFILL_SETTINGS = 'sar-autofill-settings',
  ENABLE_CGDO_COMMENTS = 'enable-cgdo-comments',
  WORKFLOW_VISUALIZER = 'workflow-visualizer',
  SHOW_SUMMER_CAMP_CAMPAIGN = 'show-smmer-camp-campaign',

  // RFI (Request for Information)
  RFI_ONBOARDING = 'RFI_ONBOARDING',
  LIVENESS_CHECK = 'LIVENESS_CHECK',

  // Queues
  QUEUE_DEADLINE_START_DATE = 'queue-deadline-start-date',
  BUSINESS_DAY_CALENDARS = 'business-day-calendars',

  // Risk Ratings
  CRR_MULTIPLE_LIVE_MODELS = 'crr-multiple-live-models',
  CRR_WATERFALL_SEGMENTATION = 'crr-waterfall-segmentation',
  CRR_TAG_FILTERS = 'crr-tag-filters',
  CRR_RISK_SCORE_FLATTENING = 'crr-risk-score-flattening',
  CRR_EXPORT_ONLY_VALIDATION = 'crr-export-only-validation',
}

export interface FeatureFlags {
  [FeatureFlag.EDIT_CUSTOM_DATA]?: boolean;
  [FeatureFlag.SHOW_PAYER_PAYEE_TABS]?: boolean;
  [FeatureFlag.REMOVE_DISPLAY_TAGS]?: boolean;
  [FeatureFlag.SIXTRAN_SNOWFLAKE_EXPECTED_LAG]?: number;
  [FeatureFlag.BULK_ACTION_LIMIT]?: number;
  [FeatureFlag.NEW_BULK_ACTIONS]?: boolean;
  [FeatureFlag.ALERT_FROM_ALERT]?: boolean;
  [FeatureFlag.RULE_EVENT_SCHEMA_V2_1]?: boolean;
  [FeatureFlag.DMB_CUSTOM_TXN_DATETIME]?: boolean;
  [FeatureFlag.SESSION_REPLAY]?: boolean;
  [FeatureFlag.WORKAROUND_HIDE_CURRENCY_CONFIG]?: boolean;
  [FeatureFlag.FRAUD_TRANSACTION_RULES]?: boolean;
  [FeatureFlag.WATCHLIST_ALERTS]?: boolean;
  [FeatureFlag.ENFORCEMENTS_SCREENING]?: boolean;
  [FeatureFlag.ACH_RISK_SCORE_FIELD]?: boolean;
  [FeatureFlag.QUALITY_CYCLE_LIMIT]?: boolean;
  [FeatureFlag.WATCHLIST_PROVIDER]?: WatchlistProviders;
  [FeatureFlag.FEEDBACK_SURVEY_ID]?: number;
  [FeatureFlag.AI_ADMIN_DASHBOARD]?: boolean;
  [FeatureFlag.FINCEN_314A]?: boolean;
  [FeatureFlag.RULE_FEEDBACK_LOOP]?: boolean;
  [FeatureFlag.AI_AGENT]?: boolean;
  [FeatureFlag.AI_AGENT_DATA_EXPLORATION]?: boolean;
  [FeatureFlag.ASK_YOUR_ALERT]?: boolean;
  [FeatureFlag.BUILD_YOUR_OWN_AGENT]?: boolean;
  [FeatureFlag.AI_AGENT_BACKTESTING]?: boolean;
  [FeatureFlag.ENABLE_FILING_AI_AGENTS]?: boolean;
  [FeatureFlag.AI_DECISIONING_V0]?: boolean;
  [FeatureFlag.ENTITY_PHONE_NUMBER_SEARCH_V2]?: boolean;
  [FeatureFlag.ALLOW_AI_AGENT_RE_RUN]?: boolean;
  [FeatureFlag.G2_REVIEW]?: boolean;
  [FeatureFlag.FINTRAC_STR]?: boolean;
  [FeatureFlag.FINTRAC_STR_UPLOAD]?: boolean;
  [FeatureFlag.FINCEN_DOEP_FILING]?: boolean;
  [FeatureFlag.GOAML_V2]?: boolean;
  [FeatureFlag.FRAUD_FIGHTER_LOGO]?: boolean;
  [FeatureFlag.SHOW_SUMMER_CAMP_CAMPAIGN]?: boolean;
  [FeatureFlag.SAR_AUTOFILL_SETTINGS]?: boolean;
  [FeatureFlag.ONLINE_RESEARCH_BYOT]?: boolean;
  [FeatureFlag.AI_AGENT_BYOT_MATCHLISTS]?: boolean;
  [FeatureFlag.G_GROUNDING_ONLINE_RESEARCH]?: boolean;
  [FeatureFlag.RULE_RECOMMENDATIONS]?: boolean;
  [FeatureFlag.RULE_RECS_V2_UI]?: boolean;
  [FeatureFlag.ALERT_COMPONENT_BUTTONS]?: boolean;
  [FeatureFlag.DISPOSITION_ACTIONS]?: boolean;
  [FeatureFlag.ALERT_TABLE_AMOUNT_COLUMN_SKELETON_LOADING]?: boolean;
  [FeatureFlag.BULK_RESOLVE_FROM_QUEUED_VIEWS]?: boolean;
  [FeatureFlag.ADD_CASES_TO_SAR]?: boolean;
  [FeatureFlag.FINTRAC_STR_AUTOFILL_WORKFLOW]?: boolean;
  [FeatureFlag.RULE_DELAY]?: boolean;
  [FeatureFlag.ACH_RISK_SCORE_SCENARIO_ENABLED]?: boolean;
  [FeatureFlag.FOCSEC_FIELDS_ENABLED]?: boolean;
  [FeatureFlag.RULE_LIFECYCLE_BANNER]?: boolean;
  [FeatureFlag.EQUIFAX_DEMO]?: boolean;
  [FeatureFlag.RULE_RECS_CREATE_RULE]?: boolean;
  [FeatureFlag.ROUND_ROBIN_GROUP_BY_ENTITY_INSTRUMENT]?: boolean;
  [FeatureFlag.ENABLE_CGDO_COMMENTS]?: boolean;
  [FeatureFlag.WORKFLOW_VISUALIZER]?: boolean;
  [FeatureFlag.WATCHLIST_COMBINED_JOURNEYS]?: boolean;
  [FeatureFlag.TEAM_BASED_WORKFLOW_PERMISSIONS]?: boolean;
  [FeatureFlag.FINTRAC_ERROR_CARDS]?: boolean;
  [FeatureFlag.RFI_ONBOARDING]?: boolean;
  [FeatureFlag.LIVENESS_CHECK]?: boolean;
  [FeatureFlag.AI_TEXT_TO_RULE]?: boolean;
  [FeatureFlag.FINCEN_ADVISORY_TEMPLATES]?: boolean;
  [FeatureFlag.GREENDOT_PIVOT_TABLES]?: boolean;
  [FeatureFlag.MCP_SERVER_ENABLED]?: boolean;
  [FeatureFlag.DMB_AI_FUZZY_SCORE]?: boolean;
  [FeatureFlag.RULES_AVOID_ALERT_GENERATION]?: boolean;
  [FeatureFlag.CLOSE_ASSOCIATED_ALERTS_ON_ESCALATION]?: boolean;

  // These are the Delta Lake Transition Flags
  [FeatureFlag.CM_DELTA_LAKE_ALERT_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ALERT_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_ANALYSIS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ALERT_ACTION_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_CGDO_SEARCH]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_TXN_ANALYSIS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_ENTITIES]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_CASE_ACTION_EVENTS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_CREATE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_CREATE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_GOAML_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_GOAML_CREATE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_GOAML_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FILING_FINTRAC_CREATE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_FRAUD_DASHBOARD]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_RELATIONS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_TXN_EVENTS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_LIST]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_DETAIL]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_RISK_SCORE]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_NETWORK_ANALYSIS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_RELATIONS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_TXN_EVENTS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_ACTION_EVENTS]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_GEOLOCATION]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_HASHED_ID_ROUTES]?: boolean;
  [FeatureFlag.CM_DELTA_LAKE_SEARCH_V2_USE_DL_HASH_ID]?: boolean;
  [FeatureFlag.FFIP_ENRICHMENT_MATCHING]?: boolean;
  [FeatureFlag.PG_CACHE_AD_HOC_DATA_LOADER]?: boolean;

  // DM Feature Flags
  [FeatureFlag.VELOCITY_RTR_ENTITY_INSTRUMENT_AGGREGATION_FILTERS]?: boolean;
  [FeatureFlag.RTR_ENTITY_ALLOWLISTING]?: boolean;
  [FeatureFlag.RTR_TRM_ENABLED]?: boolean;
  [FeatureFlag.DMB_FORM_REFACTOR]?: boolean;
  [FeatureFlag.RULE_BANKING_ACTIONS]?: boolean;
  [FeatureFlag.RULE_LOCAL_SAVE_STATE]?: boolean;
  [FeatureFlag.GLOBAL_VARIABLES]?: boolean;
  [FeatureFlag.THRESHOLD_EXPLORER]?: boolean;
  [FeatureFlag.THRESHOLD_EXPLORER_DASHBOARDS]?: boolean;
  [FeatureFlag.SR_TRM_DMB_ENABLED]?: boolean;
  [FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_ENABLED]?: boolean;
  [FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_FOR_ALL_AGENTS]?: boolean;

  // DIM
  [FeatureFlag.CONSORTIUM_DMB]?: boolean;
  [FeatureFlag.DARK_WEB_ALERTS]?: boolean;
  [FeatureFlag.CONSORTIUM_ALERTS_SUMMARY]?: boolean;
  [FeatureFlag.CONSORTIUM_ALERTS_V2]?: boolean;

  // Queues
  [FeatureFlag.QUEUE_DEADLINE_START_DATE]?: boolean;
  [FeatureFlag.BUSINESS_DAY_CALENDARS]?: boolean;

  // Risk Ratings
  [FeatureFlag.CRR_MULTIPLE_LIVE_MODELS]?: boolean;
  [FeatureFlag.CRR_WATERFALL_SEGMENTATION]?: boolean;
  [FeatureFlag.CRR_TAG_FILTERS]?: boolean;
  [FeatureFlag.CRR_RISK_SCORE_FLATTENING]?: boolean;
  [FeatureFlag.CRR_EXPORT_ONLY_VALIDATION]?: boolean;

  // We use this flag to show the loading state
  [UNLOADED_FLAGS_KEY]?: boolean;
}

/**
 * A set of feature flags to fetch and their default values.
 *
 * If the flag is not exposed to the client, the default value will be used.
 * If communication with LaunchDarkly fails, the default value will be used.
 *
 * Note: This object should contain all flags defined in the FeatureFlag enum.
 * If a flag is missing, it will not be fetched and will always be undefined.
 */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  [FeatureFlag.EDIT_CUSTOM_DATA]: false,
  [FeatureFlag.SHOW_PAYER_PAYEE_TABS]: false,
  [FeatureFlag.REMOVE_DISPLAY_TAGS]: true,
  [FeatureFlag.SIXTRAN_SNOWFLAKE_EXPECTED_LAG]: 240,
  [FeatureFlag.BULK_ACTION_LIMIT]: 100,
  [FeatureFlag.NEW_BULK_ACTIONS]: false,
  [FeatureFlag.ALERT_FROM_ALERT]: false,
  [FeatureFlag.RULE_EVENT_SCHEMA_V2_1]: true,
  [FeatureFlag.DMB_CUSTOM_TXN_DATETIME]: false,
  [FeatureFlag.SESSION_REPLAY]: false,
  [FeatureFlag.WORKAROUND_HIDE_CURRENCY_CONFIG]: false,
  [FeatureFlag.FRAUD_TRANSACTION_RULES]: false,
  [FeatureFlag.WATCHLIST_ALERTS]: false,
  [FeatureFlag.ENFORCEMENTS_SCREENING]: false,
  [FeatureFlag.ACH_RISK_SCORE_FIELD]: false,
  [FeatureFlag.QUALITY_CYCLE_LIMIT]: true,
  [FeatureFlag.WATCHLIST_PROVIDER]: WatchlistProviders.LEXISNEXIS,
  [FeatureFlag.FEEDBACK_SURVEY_ID]: 41877568,
  [FeatureFlag.AI_ADMIN_DASHBOARD]: false,
  [FeatureFlag.FINCEN_314A]: false,
  [FeatureFlag.RULE_FEEDBACK_LOOP]: false,
  [FeatureFlag.AI_AGENT]: false,
  [FeatureFlag.AI_AGENT_DATA_EXPLORATION]: false,
  [FeatureFlag.ASK_YOUR_ALERT]: false,
  [FeatureFlag.BUILD_YOUR_OWN_AGENT]: false,
  [FeatureFlag.AI_AGENT_BACKTESTING]: false,
  [FeatureFlag.ENABLE_FILING_AI_AGENTS]: false,
  [FeatureFlag.ENTITY_PHONE_NUMBER_SEARCH_V2]: false,
  [FeatureFlag.ALLOW_AI_AGENT_RE_RUN]: false,
  [FeatureFlag.G2_REVIEW]: false,
  [FeatureFlag.FINTRAC_STR]: false,
  [FeatureFlag.FINTRAC_STR_UPLOAD]: false,
  [FeatureFlag.FINCEN_DOEP_FILING]: false,
  [FeatureFlag.GOAML_V2]: false,
  [FeatureFlag.FRAUD_FIGHTER_LOGO]: false,
  [FeatureFlag.SHOW_SUMMER_CAMP_CAMPAIGN]: false,
  [FeatureFlag.SAR_AUTOFILL_SETTINGS]: false,
  [FeatureFlag.ONLINE_RESEARCH_BYOT]: false,
  [FeatureFlag.AI_AGENT_BYOT_MATCHLISTS]: false,
  [FeatureFlag.G_GROUNDING_ONLINE_RESEARCH]: false,
  [FeatureFlag.RULE_RECOMMENDATIONS]: false,
  [FeatureFlag.RULE_RECS_V2_UI]: false,
  [FeatureFlag.ALERT_COMPONENT_BUTTONS]: false,
  [FeatureFlag.DISPOSITION_ACTIONS]: false,
  [FeatureFlag.ALERT_TABLE_AMOUNT_COLUMN_SKELETON_LOADING]: false,
  [FeatureFlag.BULK_RESOLVE_FROM_QUEUED_VIEWS]: false,
  [FeatureFlag.ADD_CASES_TO_SAR]: false,
  [FeatureFlag.FINTRAC_STR_AUTOFILL_WORKFLOW]: false,
  [FeatureFlag.AI_DECISIONING_V0]: false,
  [FeatureFlag.RULE_DELAY]: false,
  [FeatureFlag.ACH_RISK_SCORE_SCENARIO_ENABLED]: false,
  [FeatureFlag.FOCSEC_FIELDS_ENABLED]: true,
  [FeatureFlag.RULE_LIFECYCLE_BANNER]: false,
  [FeatureFlag.EQUIFAX_DEMO]: false,
  [FeatureFlag.RULE_RECS_CREATE_RULE]: false,
  [FeatureFlag.ROUND_ROBIN_GROUP_BY_ENTITY_INSTRUMENT]: false,
  [FeatureFlag.ENABLE_CGDO_COMMENTS]: true,
  [FeatureFlag.WORKFLOW_VISUALIZER]: false,
  [FeatureFlag.WATCHLIST_COMBINED_JOURNEYS]: false,
  [FeatureFlag.TEAM_BASED_WORKFLOW_PERMISSIONS]: false,
  [FeatureFlag.FINTRAC_ERROR_CARDS]: false,
  [FeatureFlag.RFI_ONBOARDING]: false,
  [FeatureFlag.LIVENESS_CHECK]: false,
  [FeatureFlag.AI_TEXT_TO_RULE]: false,
  [FeatureFlag.FINCEN_ADVISORY_TEMPLATES]: false,
  [FeatureFlag.GREENDOT_PIVOT_TABLES]: false,
  [FeatureFlag.MCP_SERVER_ENABLED]: false,
  [FeatureFlag.DMB_AI_FUZZY_SCORE]: false,

  // These are the Delta Lake Transition Flags
  [FeatureFlag.CM_DELTA_LAKE_ALERT_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_ALERT_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_ANALYSIS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ALERT_TXN_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_ALERT_ACTION_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_CGDO_SEARCH]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_TXN_ANALYSIS]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_ENTITIES]: false,
  [FeatureFlag.CM_DELTA_LAKE_CASE_ACTION_EVENTS]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_CREATE]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_CREATE]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_GOAML_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_GOAML_CREATE]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_SAR_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_CTR_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_GOAML_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_FILING_FINTRAC_CREATE]: false,
  [FeatureFlag.CM_DELTA_LAKE_FRAUD_DASHBOARD]: false,
  [FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_TXN_EVENT_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_ACTION_EVENT_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_RELATIONS]: false,
  [FeatureFlag.CM_DELTA_LAKE_INSTRUMENT_TXN_EVENTS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_LIST]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_DETAIL]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_RISK_SCORE]: false,
  [FeatureFlag.CM_DELTA_LAKE_NETWORK_ANALYSIS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_RELATIONS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_TXN_EVENTS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_ACTION_EVENTS]: false,
  [FeatureFlag.CM_DELTA_LAKE_ENTITY_GEOLOCATION]: false,
  [FeatureFlag.CM_DELTA_LAKE_HASHED_ID_ROUTES]: false,
  [FeatureFlag.CM_DELTA_LAKE_SEARCH_V2_USE_DL_HASH_ID]: false,
  [FeatureFlag.FFIP_ENRICHMENT_MATCHING]: false,
  [FeatureFlag.PG_CACHE_AD_HOC_DATA_LOADER]: false,
  [FeatureFlag.CLOSE_ASSOCIATED_ALERTS_ON_ESCALATION]: false,

  // DM Feature Flags
  [FeatureFlag.VELOCITY_RTR_ENTITY_INSTRUMENT_AGGREGATION_FILTERS]: false,
  [FeatureFlag.RTR_ENTITY_ALLOWLISTING]: false,
  [FeatureFlag.RTR_TRM_ENABLED]: false,
  [FeatureFlag.DMB_FORM_REFACTOR]: false,
  [FeatureFlag.RULE_BANKING_ACTIONS]: false,
  [FeatureFlag.RULE_LOCAL_SAVE_STATE]: false,
  [FeatureFlag.GLOBAL_VARIABLES]: false,
  [FeatureFlag.THRESHOLD_EXPLORER]: false,
  [FeatureFlag.THRESHOLD_EXPLORER_DASHBOARDS]: false,
  [FeatureFlag.SR_TRM_DMB_ENABLED]: false,
  [FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_ENABLED]: false,
  [FeatureFlag.DM_ALERT_HIT_CGDO_HISTORICAL_LOOKUP_FOR_ALL_AGENTS]: false,

  // DIM
  [FeatureFlag.DARK_WEB_ALERTS]: false,
  [FeatureFlag.CONSORTIUM_DMB]: false,
  [FeatureFlag.CONSORTIUM_ALERTS_SUMMARY]: false,
  [FeatureFlag.CONSORTIUM_ALERTS_V2]: false,

  // Queues
  [FeatureFlag.QUEUE_DEADLINE_START_DATE]: false,
  [FeatureFlag.BUSINESS_DAY_CALENDARS]: false,

  // Risk Ratings
  [FeatureFlag.CRR_MULTIPLE_LIVE_MODELS]: false,
  [FeatureFlag.CRR_WATERFALL_SEGMENTATION]: false,
  [FeatureFlag.CRR_TAG_FILTERS]: false,
  [FeatureFlag.CRR_RISK_SCORE_FLATTENING]: false,
  [FeatureFlag.CRR_EXPORT_ONLY_VALIDATION]: false,

  // Rules
  [FeatureFlag.RULES_AVOID_ALERT_GENERATION]: false,
} as const satisfies Record<FeatureFlag, string | number | boolean>;

export interface FeatureFlagsState {
  flags: FeatureFlags;
}
